<template>
  <v-container class="pa-0">
    <v-tabs v-model="activeTab" class="custom-tabs">
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        class="custom-tab"
        :class="{ 'custom-tab--active': activeTab === index }"
        @click="changeTab(index)"
      >
        {{ tab.name }}
      </div>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <v-tab-item
        v-for="(tab, index) in tabs"
        :key="index"
      >
        <v-expansion-panels
          v-model="expandedPanels[index]"
          flat
          class="table-expansion-panel"
        >
        <!-- Header -->
          <v-expansion-panel>
            <div class="tab-header">
              <h3>{{ tab.headerLabel || tab.name }}</h3>
              <div>
                <v-btn
                  v-for="(button, btnIndex) in tab.headerButtons"
                  :key="btnIndex"
                  :color="button.color || 'white'"
                  :plain="button.plain"
                  :disabled="button.disabled"
                  @click="handleButtonClick(button.emitName, index)"
                >
                  <v-icon v-if="button.iconName" :color="button.color || 'white'">
                    {{ button.iconName }}
                  </v-icon>
                  {{ button.label }}
                </v-btn>
                <v-btn
                  icon
                  @click="toggleExpansion(index)"
                >
                  <v-icon color="white">
                    {{ expandedPanels[index] === 0 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                </v-btn>
              </div>
            </div>
            <!-- Table -->
            <v-expansion-panel-content class="pa-0">
              <v-card-text class="pa-0 table-container">
                <v-data-table
                  :headers="tab.tableHeaders"
                  :items="tab.tableData"
                  :items-per-page="tab.itemsPerPage || 10"
                  :sort-by="tab.sortBy"
                  :sort-desc="tab.sortDesc"
                  :height="tab.tableHeight || 'auto'"
                >
                  <template #item.actions="{ item }">
                    <v-btn
                      v-for="(action, actionIndex) in tab.itemActions"
                      :key="actionIndex"
                      :color="action.color || 'white'"
                      :plain="action.plain"
                      :disabled="action.disabled"
                      @click="handleButtonClick(action.emitName, index, item)"
                    >
                      <v-icon v-if="action.iconName">
                        {{ action.iconName }}
                      </v-icon>
                      {{ action.label }}
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import { ref, reactive } from '@vue/composition-api';

export default {
  name: 'TabbedDataTable',
  props: {
    /**
     * Array of tab configurations
     * Each tab object should contain:
     * @param {number} index - Tab index
     * @param {string} name - Tab display name
     * @param {string} headerLabel - Optional title for the header bar (defaults to name)
     * @param {Array} headerButtons - Optional buttons for the header
     * @param {Array} tableHeaders - Data table headers
     * @param {Array} tableData - Data items for the table
     * @param {Array} itemActions - Optional actions for the table items
     * @param {number} itemsPerPage - Items per page (default: 10)
     * @param {string|Array} sortBy - Default sort column(s)
     * @param {boolean|Array} sortDesc - Default sort direction(s)
     */
    tabs: {
      type: Array,
      required: true,
      default: () => []
    },
    initialTab: {
      type: Number,
      default: 0
    }
  },
  setup (props, { emit }) {
    const activeTab = ref(props.initialTab);
    
    const initialPanels = {};

    props.tabs.forEach((_, index) => {
      initialPanels[index] = 0;
    });

    const expandedPanels = reactive(initialPanels);

    const changeTab = (tabIndex) => {
      activeTab.value = tabIndex;
    };

    const toggleExpansion = (tabIndex) => {
      expandedPanels[tabIndex] = expandedPanels[tabIndex] === 0 ? null : 0;
    };

    const handleButtonClick = (emitName, tabIndex, item = null) => {
      emit(emitName, { tabIndex, item });
    };

    return {
      activeTab,
      expandedPanels,
      toggleExpansion,
      handleButtonClick,
      changeTab
    };
  }
};
</script>

<style scoped>
  .custom-tabs {
    display: flex;
    border-bottom: 1px solid #ffffff;
  }

  .custom-tab {
    padding: 12px 24px;
    cursor: pointer;
    background-color: #ffffff;
    border: 1px solid #c62828;
    border-radius: 4px;
    margin-right: 2px;
    transition: all 0.3s ease;
    color: #c62828;
  }

  .custom-tab--active {
    background-color: #c62828;
    color: white;
    border-color: #c62828;
  }

  .table-expansion-panel >>> .v-expansion-panel-content__wrap, .table-expansion-panel >>> .v-expansion-panel-header {
    padding: 0;
  }

  .tab-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    background-color: #c62828;
    color: white;
  }

  .table-container {
    border-left: 1px solid #e0e0e0;
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
</style>

